import { useCallback, useEffect, useState } from 'react'
import { skyApi, type SkyCanvass } from '../api/sky'
import { SKY_RETE_LABEL, dataIt } from '../lib/skyPromo'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, StatusPill } from './ui'

/**
 * Stato della lettura del canvass PROMO SKY (Impostazioni › Integrazioni): quale file è in uso, quando è stato letto e
 * controllato, le promo trovate; «Rileggi ora» scarica il PDF da Drive e, se è cambiato, lo fa rileggere all'IA.
 */
const quando = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

export function SkyCanvassCard() {
  const [c, setC] = useState<SkyCanvass | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [mostraPromo, setMostraPromo] = useState(false)

  const load = useCallback(async () => {
    try { setC(await skyApi.canvass()); setError(null) }
    catch (e) { setError(e instanceof Error ? e.message : 'Errore nel caricamento') }
  }, [])
  useEffect(() => { void load() }, [load])

  async function refresh() {
    setBusy(true)
    setMsg(null)
    try {
      const r = await skyApi.refreshCanvass()
      setC(r.stato)
      setMsg(r.esito === 'letto' ? `Canvass nuovo letto: ${r.promo} promo da «${r.file}».`
        : r.esito === 'invariato' ? `Il file su Drive è ancora «${r.file}»: promo invariate.`
        : r.errore ?? 'Controllo non riuscito.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rilettura non riuscita')
    } finally {
      setBusy(false)
    }
  }

  const tone = !c ? 'neutral' : c.stato === 'ok' ? 'success' : c.stato === 'errore' ? 'error' : 'warning'
  const stato = !c ? '' : c.stato === 'ok' ? 'Aggiornato' : c.stato === 'errore' ? 'Ultimo controllo fallito' : c.stato === 'in_lettura' ? 'In lettura' : 'Mai letto'

  return (
    <Card>
      <CardHeader title="Canvass PROMO SKY" actions={
        <Button type="button" variant="text" size="sm" disabled={busy} loading={busy} onClick={() => { void refresh() }}>Rileggi ora</Button>
      } />
      <CardContent>
        <p className="md-typescale-body-small">
          Ogni giorno il CRM controlla la cartella Drive dei dealer Sky: se il PDF «PROMO SKY» del mese è cambiato lo fa
          leggere all'IA e aggiorna gli ID promo proposti nelle pratiche Sky (per offerta, tipo di cliente e rete di copertura).
        </p>
        {error && <Callout tone="error">{error}</Callout>}
        {msg && <Callout tone={c?.stato === 'errore' ? 'warning' : 'info'}>{msg}</Callout>}
        {c && (
          <>
            <div className="d-flex flex-wrap align-items-center gap-3 md-typescale-body-small">
              <StatusPill tone={tone}>{stato}</StatusPill>
              <span>File: <strong>{c.file ?? '—'}</strong>{c.etichetta ? ` (${c.etichetta})` : ''}</span>
              <span>Letto il {quando(c.lettoIl)}</span>
              <span>Controllato il {quando(c.controllatoIl)}</span>
              <span>{c.promo.length} promo</span>
            </div>
            {!c.configurato && <Callout tone="warning" className="mt-2">Manca la chiave dell'IA: impostala in Impostazioni › IA, altrimenti il canvass non si può leggere.</Callout>}
            {c.stato === 'errore' && c.errore && <Callout tone="error" className="mt-2">{c.errore}</Callout>}
            {c.regolaCopertura && (
              <p className="md-typescale-body-small text-muted mt-2 mb-0">Regola di copertura del canvass: {c.regolaCopertura}</p>
            )}
            {c.promo.length > 0 && (
              <div className="mt-2">
                <Button type="button" variant="text" size="sm" onClick={() => setMostraPromo(m => !m)}>
                  {mostraPromo ? 'Nascondi le promo' : `Mostra le promo (${c.promo.length})`}
                </Button>
                {mostraPromo && (
                  <DataTable variant="plain" aria-label="Promo del canvass Sky">
                    <thead><tr><th>Codice</th><th>Offerta</th><th>Variante</th><th>Per</th><th>Rete</th><th>Prezzo</th><th>Attivazione</th><th>Scadenza</th><th>Pag.</th></tr></thead>
                    <tbody>
                      {c.promo.map((p, i) => (
                        <tr key={`${p.codice}-${i}`}>
                          <td><strong>{p.codice}</strong></td>
                          <td>{p.offerta}</td>
                          <td>{p.variante ?? ''}</td>
                          <td className="md-typescale-body-small">{[p.tipologia, p.segmento].join(' · ')}</td>
                          <td>{SKY_RETE_LABEL[p.rete] ?? p.rete}</td>
                          <td>{p.prezzo ?? ''}{p.durata ? ` per ${p.durata}` : ''}</td>
                          <td>{p.attivazione ?? ''}</td>
                          <td>{dataIt(p.scadenza)}</td>
                          <td>{p.pagina ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
